import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Home, Dumbbell, X, Loader2, ChevronLeft, ChevronRight,
  Heart, Mountain, Flame, Wind, SkipForward,
} from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { api, useAuth } from '../context/AppContext';
import { toast } from 'sonner';

/**
 * WorkoutOnboardingModal — J5 wizard (Iter. 405.h).
 *
 * 6-step guided questionnaire that feeds the existing
 * POST /api/workouts/generate endpoint and the AI-workout-generator
 * service. Designed to make J5 of the welcome challenge approachable
 * for absolute beginners while ALSO respecting advanced users.
 *
 * Yuri's 3 critical constraints (Iter. 405.h):
 *   1. Non-blocking — "Passer cette étape" button on every screen
 *      maps to POST /welcome-challenge/skip-day. The journey continues.
 *   2. Tone adapts to level — debutant gets imagery/no jargon,
 *      intermediaire/avance gets the pro vocabulary. Plumbed via the
 *      `onboarding_mode=true` flag the backend reads off the request.
 *   3. Plan persists 14 days — the generated plan lives in
 *      db.ai_workout_plans (already!). Trial users keep accessing it
 *      via /api/workouts/active. After 14 days require_active_access
 *      enforces the paywall on NEW generations.
 *
 * Props
 *   open         bool  — controlled visibility
 *   onClose      fn    — close & reset
 *   onSuccess    fn(plan) — fired after a plan is generated (parent can
 *                            navigate to /workouts to render it)
 *   onSkipped    fn    — fired after the user explicitly skips J5
 *                          (parent should refresh the welcome status)
 */
const STEPS = ['location', 'equipment', 'level', 'goal', 'duration', 'limitations'];

const LOCATION_OPTIONS = [
  { key: 'home', label: { fr: 'À la maison', en: 'At home' }, Icon: Home },
  { key: 'gym', label: { fr: 'Au gym', en: 'At the gym' }, Icon: Dumbbell },
];

const EQUIPMENT_OPTIONS = [
  { key: 'poids_du_corps', emoji: '👤', label: { fr: 'Poids du corps', en: 'Bodyweight' } },
  { key: 'elastiques', emoji: '🟣', label: { fr: 'Élastiques', en: 'Resistance bands' } },
  { key: 'halteres', emoji: '🟦', label: { fr: 'Haltères de base', en: 'Basic dumbbells' } },
  { key: 'gym_complet', emoji: '🏋️', label: { fr: 'Gym complet', en: 'Full gym' } },
];

const LEVEL_OPTIONS = [
  {
    key: 'debutant',
    emoji: '🌱',
    label: { fr: 'Débutant', en: 'Beginner' },
    desc: { fr: 'Focus posture', en: 'Posture focus' },
  },
  {
    key: 'intermediaire',
    emoji: '⚡',
    label: { fr: 'Intermédiaire', en: 'Intermediate' },
    desc: { fr: 'Je connais les bases', en: "I know the basics" },
  },
  {
    key: 'avance',
    emoji: '🔥',
    label: { fr: 'Avancé', en: 'Advanced' },
    desc: { fr: 'Je maîtrise la technique', en: 'Solid technique' },
  },
];

const GOAL_OPTIONS = [
  { key: 'tonify', Icon: Sparkles, label: { fr: 'Tonifier et raffermir', en: 'Tone & sculpt' } },
  { key: 'strength', Icon: Mountain, label: { fr: 'Devenir plus fort', en: 'Get stronger' } },
  { key: 'burn', Icon: Flame, label: { fr: 'Brûler des calories', en: 'Burn calories' } },
  { key: 'move', Icon: Wind, label: { fr: 'Bouger pour me sentir bien', en: 'Just move & feel good' } },
];

const DURATION_OPTIONS = [20, 30, 45, 60];

// Iter. 406.x — Removed the 5-chip generic limitation list (genoux/dos/épaule/
// poignet/cheville). Replaced by a free-text Textarea in the `limitations`
// step so the client can describe injuries with the same precision as the
// Coach interface (hernie discale L4-L5, tendinite à l'épaule droite,
// douleur aux genoux en flexion, etc.). The AI prompt block in
// `ai_workout_generator.py` consumes this verbatim with TOP priority.

const Title = ({ children }) => (
  <h2 className="text-xl font-heading uppercase tracking-tight text-foreground leading-tight">{children}</h2>
);

export default function WorkoutOnboardingModal({ open, onClose, onSuccess, onSkipped, language = 'fr' }) {
  const [stepIdx, setStepIdx] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [answers, setAnswers] = useState({
    location: null,
    equipment: null,
    intensity_level: null,
    objective: null,
    max_duration_minutes: null,
    // Iter. 406.x — Free-text injury description (verbatim) — same precision
    // and priority as the Coach interface.
    injuries_description: '',
  });
  const [generating, setGenerating] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const step = STEPS[stepIdx];
  const totalSteps = STEPS.length;

  const reset = () => {
    setStepIdx(0);
    setAnswers({
      location: null, equipment: null, intensity_level: null,
      objective: null, max_duration_minutes: null, injuries_description: '',
    });
  };

  const handleClose = () => {
    if (generating || skipping) return;
    reset();
    onClose();
  };

  const canAdvance = () => {
    if (step === 'location') return !!answers.location;
    if (step === 'equipment') return !!answers.equipment;
    if (step === 'level') return !!answers.intensity_level;
    if (step === 'goal') return !!answers.objective;
    if (step === 'duration') return !!answers.max_duration_minutes;
    if (step === 'limitations') return true; // always optional
    return false;
  };

  const handleSkipMission = async () => {
    setSkipping(true);
    try {
      await api.post('/welcome-challenge/skip-day', { day: 5, reason: 'nutrition_only' });
      toast.info(
        language === 'fr'
          ? 'Mission J5 passée. Direction le jour suivant !'
          : 'J5 skipped — onto the next day!',
        { duration: 3500 }
      );
      if (onSkipped) onSkipped();
      reset();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || (
        language === 'fr' ? 'Impossible de passer cette étape pour le moment.' : 'Could not skip right now.'
      );
      toast.error(detail);
    } finally {
      setSkipping(false);
    }
  };

  const handleGenerate = async () => {
    if (!user?.user_id) {
      toast.error(language === 'fr' ? 'Session expirée — reconnecte-toi.' : 'Session expired — sign in again.');
      return;
    }
    setGenerating(true);
    try {
      // Iter. 406.fh — Onboarding now hits the SAME Armurerie engine the
      // Coach uses in "Mon Plan". We generate a real multi-week plan and
      // deploy it immediately (self-mode authorised by the backend).
      // Map the onboarding answers to the Armurerie payload.
      const GOAL_TO_FOCUS = {
        tonify: 'general',
        strength: 'upper',
        burn: 'cardio',
        move: 'general',
      };
      const equipmentMode = (answers.equipment === 'gym_complet' || answers.location === 'gym') ? 'gym' : 'home';
      const equipmentDetail = {
        poids_du_corps: 'Poids du corps uniquement',
        elastiques: 'Bandes élastiques uniquement',
        halteres: 'Haltères de base + poids du corps',
        gym_complet: 'Gym complet (barres, haltères, machines, câbles)',
      }[answers.equipment] || '';
      const levelLabel = { debutant: 'Débutant', intermediaire: 'Intermédiaire', avance: 'Avancé' }[answers.intensity_level] || 'Intermédiaire';
      const goalLabel = { tonify: 'tonifier/raffermir', strength: 'devenir plus fort', burn: 'brûler des calories', move: 'bouger pour bien-être' }[answers.objective] || answers.objective;

      const directiveParts = [
        `Niveau: ${levelLabel}`,
        `Objectif principal: ${goalLabel}`,
        `Durée par séance souhaitée: ${answers.max_duration_minutes} minutes`,
        equipmentDetail ? `Équipement précis: ${equipmentDetail}` : '',
        (answers.injuries_description || '').trim() ? `Blessures/limitations déclarées par le client: ${answers.injuries_description.trim()}` : '',
      ].filter(Boolean);
      const coachDirectives = directiveParts.join(' | ');

      const daysPerWeek = answers.intensity_level === 'debutant' ? 3 : 4;
      const durationWeeks = 4;

      const genPayload = {
        focus: GOAL_TO_FOCUS[answers.objective] || 'general',
        days_per_week: daysPerWeek,
        duration_weeks: durationWeeks,
        equipment: equipmentMode,
        notes: `Onboarding J5 - première séance multi-semaines pour ${user?.name || 'le client'}.`,
        coach_directives: coachDirectives,
      };

      const genRes = await api.post(`/coach/command/armurerie/${user.user_id}/generate`, genPayload);
      if (!genRes.data?.success || !genRes.data?.draft_plan) {
        throw new Error(genRes.data?.error || 'AI generation failed');
      }
      const draftPlan = genRes.data.draft_plan;

      // Persist injury description on the user profile so future generations
      // pick it up automatically — parity with the legacy /workouts/generate
      // side-effect from Iter. 406.x.
      if ((answers.injuries_description || '').trim()) {
        try {
          await api.patch('/users/me/injuries-description', {
            injuries_description: answers.injuries_description.trim(),
          });
        } catch { /* non-blocking */ }
      }

      const deployRes = await api.post(`/coach/command/armurerie/${user.user_id}/deploy`, {
        plan: draftPlan,
        duration_weeks: genRes.data.duration_weeks || durationWeeks,
      });
      if (!deployRes.data?.success) {
        throw new Error(deployRes.data?.message || 'Deploy failed');
      }

      toast.success(
        language === 'fr' ? 'Ton plan est prêt et activé !' : 'Your plan is ready and active!',
        { duration: 4000 }
      );

      if (onSuccess) onSuccess({ plan_id: deployRes.data.plan_id, draft_plan: draftPlan });
      reset();
      onClose();

      // Iter. 406.fh — Cache invalidation: broadcast a global event so any
      // mounted MyPlanTab / Dashboard widget refetches `/training-plans/active`
      // WITHOUT requiring the user to manually refresh or change tabs.
      try { window.dispatchEvent(new CustomEvent('yumix:plan-activated', { detail: { plan_id: deployRes.data.plan_id } })); } catch { /* ignore */ }
      try { window.dispatchEvent(new Event('yumix:onboarding-refresh')); } catch { /* ignore */ }

      // Force redirect to "Mon Plan" so the user IMMEDIATELY sees their plan.
      try { navigate('/workouts?tab=plan'); } catch { /* navigation safety net */ }
    } catch (err) {
      const data = err.response?.data;
      const detailObj = data?.detail;
      const detail = detailObj || data?.error || err.message || (
        language === 'fr' ? 'Impossible de générer le plan pour l\'instant.' : 'Could not generate the plan.'
      );
      const code = (typeof detailObj === 'object' && detailObj?.error_code) || data?.error_code;
      console.error('[WorkoutOnboardingGen] failure', { code, detail, raw: data });
      // Iter. 406.fi — Surface the atomic safety-net rejection with a clear
      // "your current plan was preserved" message + a one-click retry that
      // re-runs the generation with a fresh seed.
      const isEmptyReject = code === 'empty_plan_rejected' || code === 'empty_plan_after_validation';
      const msg = isEmptyReject
        ? (language === 'fr'
            ? 'Échec de génération — ton plan actuel a été conservé. Réessaie ou ajuste ton équipement.'
            : 'Generation failed — your current plan was kept. Try again or change your equipment.')
        : (typeof detail === 'string' ? detail : (detail?.error || JSON.stringify(detail)));
      toast.error(msg, {
        duration: 10000,
        action: isEmptyReject ? {
          label: language === 'fr' ? 'Réessayer' : 'Retry',
          onClick: () => { handleGenerate(); },
        } : undefined,
      });
    } finally {
      setGenerating(false);
    }
  };

  const next = () => {
    if (!canAdvance()) return;
    if (stepIdx === totalSteps - 1) {
      handleGenerate();
    } else {
      setStepIdx((s) => Math.min(s + 1, totalSteps - 1));
    }
  };

  const prev = () => setStepIdx((s) => Math.max(s - 1, 0));

  const renderStep = () => {
    if (step === 'location') {
      return (
        <>
          <Title>{language === 'fr' ? "Où t'entraînes-tu ?" : 'Where are you training?'}</Title>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {LOCATION_OPTIONS.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setAnswers((a) => ({ ...a, location: key }))}
                data-testid={`onboarding-location-${key}`}
                className={`p-5 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                  answers.location === key
                    ? 'border-primary bg-primary/10 scale-[1.02]'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                <Icon className="w-8 h-8" />
                <span className="text-sm font-semibold">{label[language] || label.fr}</span>
              </button>
            ))}
          </div>
        </>
      );
    }
    if (step === 'equipment') {
      return (
        <>
          <Title>{language === 'fr' ? 'Ton équipement ?' : 'Your equipment?'}</Title>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {EQUIPMENT_OPTIONS.map(({ key, emoji, label }) => (
              <button
                key={key}
                onClick={() => setAnswers((a) => ({ ...a, equipment: key }))}
                data-testid={`onboarding-equipment-${key}`}
                className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                  answers.equipment === key
                    ? 'border-primary bg-primary/10 scale-[1.02]'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                <span className="text-3xl">{emoji}</span>
                <span className="text-xs font-semibold text-center leading-tight">{label[language] || label.fr}</span>
              </button>
            ))}
          </div>
        </>
      );
    }
    if (step === 'level') {
      return (
        <>
          <Title>{language === 'fr' ? 'Ton niveau ?' : 'Your level?'}</Title>
          <div className="space-y-3 mt-4">
            {LEVEL_OPTIONS.map(({ key, emoji, label, desc }) => (
              <button
                key={key}
                onClick={() => setAnswers((a) => ({ ...a, intensity_level: key }))}
                data-testid={`onboarding-level-${key}`}
                className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center gap-3 text-left ${
                  answers.intensity_level === key
                    ? 'border-primary bg-primary/10 scale-[1.01]'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                <span className="text-3xl shrink-0">{emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold leading-tight">{label[language] || label.fr}</p>
                  <p className="text-xs text-muted-foreground leading-tight">{desc[language] || desc.fr}</p>
                </div>
              </button>
            ))}
          </div>
        </>
      );
    }
    if (step === 'goal') {
      return (
        <>
          <Title>{language === 'fr' ? 'Aujourd\'hui je veux…' : 'Today I want to…'}</Title>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {GOAL_OPTIONS.map(({ key, Icon, label }) => (
              <button
                key={key}
                onClick={() => setAnswers((a) => ({ ...a, objective: key }))}
                data-testid={`onboarding-goal-${key}`}
                className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 min-h-[100px] ${
                  answers.objective === key
                    ? 'border-primary bg-primary/10 scale-[1.02]'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                <Icon className="w-7 h-7" />
                <span className="text-xs font-semibold text-center leading-tight">{label[language] || label.fr}</span>
              </button>
            ))}
          </div>
        </>
      );
    }
    if (step === 'duration') {
      return (
        <>
          <Title>{language === 'fr' ? 'Combien de temps ?' : 'How much time?'}</Title>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {DURATION_OPTIONS.map((m) => (
              <button
                key={m}
                onClick={() => setAnswers((a) => ({ ...a, max_duration_minutes: m }))}
                data-testid={`onboarding-duration-${m}`}
                className={`py-6 rounded-2xl border-2 transition-all text-2xl font-bold ${
                  answers.max_duration_minutes === m
                    ? 'border-primary bg-primary/10 text-primary scale-[1.02]'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                {m} min
              </button>
            ))}
          </div>
        </>
      );
    }
    if (step === 'limitations') {
      // Iter. 406.x — Free-text injury textarea. Coach-grade question.
      const txt = answers.injuries_description || '';
      const len = txt.trim().length;
      return (
        <>
          <Title>
            {language === 'fr'
              ? 'Avez-vous des blessures, des douleurs ou des limitations physiques ?'
              : 'Any injuries, pain or physical limitations?'}
          </Title>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            {language === 'fr'
              ? 'Décris-les avec précision pour que l\'IA adapte chaque exercice, angle et machine.'
              : 'Describe them precisely so the AI tailors every exercise, angle and machine.'}
          </p>
          <textarea
            value={txt}
            onChange={(e) => setAnswers((a) => ({ ...a, injuries_description: e.target.value }))}
            placeholder={language === 'fr'
              ? 'Ex: Hernie discale lombaire, tendinite à l\'épaule droite, douleur aux genoux en faisant des squats, etc. (Laissez vide si aucune).'
              : 'E.g. lumbar disc herniation, right shoulder tendinitis, knee pain on squats, etc. (Leave empty if none).'}
            rows={6}
            maxLength={1200}
            className="mt-4 w-full rounded-xl border-2 border-border bg-secondary/30 px-4 py-3 text-sm leading-relaxed focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground/50"
            data-testid="onboarding-injuries-textarea"
          />
          <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
            <span>
              {len === 0
                ? (language === 'fr' ? 'Aucune limitation — go !' : 'No limits — go!')
                : (language === 'fr'
                    ? `🛡️ L'IA va adapter chaque exercice pour protéger ces zones`
                    : '🛡️ The AI will adapt every exercise to protect these zones')}
            </span>
            <span className="tabular-nums">{len}/1200</span>
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="bg-card border-border max-w-md p-0 overflow-hidden"
        data-testid="workout-onboarding-modal"
      >
        {/* Progress bar */}
        <div className="h-1 bg-secondary/50 w-full">
          <div
            className="h-full bg-gradient-to-r from-primary via-orange-500 to-pink-500 transition-all duration-300"
            style={{ width: `${((stepIdx + 1) / totalSteps) * 100}%` }}
          />
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {language === 'fr' ? `Étape ${stepIdx + 1} / ${totalSteps}` : `Step ${stepIdx + 1} / ${totalSteps}`}
            </span>
            <button
              type="button"
              onClick={handleClose}
              disabled={generating || skipping}
              className="p-1 rounded-full hover:bg-secondary disabled:opacity-30"
              aria-label="Fermer"
              data-testid="workout-onboarding-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="min-h-[260px]">{renderStep()}</div>

          {/* Footer */}
          <div className="mt-5 space-y-3">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={prev}
                disabled={stepIdx === 0 || generating || skipping}
                className="px-4"
                data-testid="workout-onboarding-back"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                onClick={next}
                disabled={!canAdvance() || generating || skipping}
                className="flex-1 bg-primary text-primary-foreground"
                data-testid="workout-onboarding-next"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {language === 'fr' ? 'Génération…' : 'Generating…'}</>
                ) : stepIdx === totalSteps - 1 ? (
                  <><Heart className="w-4 h-4 mr-2" /> {language === 'fr' ? 'Générer ma séance' : 'Generate my workout'}</>
                ) : (
                  <>{language === 'fr' ? 'Continuer' : 'Continue'} <ChevronRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            </div>

            <button
              type="button"
              onClick={handleSkipMission}
              disabled={generating || skipping}
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-2 flex items-center justify-center gap-1.5 disabled:opacity-40"
              data-testid="workout-onboarding-skip"
            >
              {skipping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SkipForward className="w-3.5 h-3.5" />}
              {language === 'fr'
                ? 'Passer cette étape (focus nutrition & mindset)'
                : 'Skip this step (nutrition & mindset only)'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
